import type {RuleValidationErrors} from '../types';
import {attributesStore} from '@netx/core-attribute/store';
import {validators} from '@netx/core-attribute/validators';
import {MetadataType, MetadataNoValueTypes} from './constants';
import type {MetadataRule} from './types';
import {createValidationObject} from '../../utils/createValidationObject';

const stringIsDate = (str: string) =>
	str.match(/\d{4}-\d{1,2}-\d{1,2}/) != null;

export const validateMetadataRule = <
	T extends MetadataRule<any> = MetadataRule<any>,
>(
	rule: T,
): RuleValidationErrors | false => {
	const hasElementValue1 = Array.isArray(rule.elementValue1)
		? !!rule.elementValue1.length
		: rule.elementValue1 !== '';
	const hasElementValue2 = rule.elementValue2 !== '';
	const hasElementValue3 = rule.elementValue3 !== '';

	const attribute = attributesStore.getAttributeByName(rule.elementValue2);
	const validator =
		attribute && validators[attribute.type as keyof typeof validators];

	const errors: RuleValidationErrors = {};

	const validate = (
		item: 'elementValue1' | 'elementValue2' | 'elementValue3',
	) => {
		try {
			// we must assume that this is fine since we cannot validate any further
			// without the attribute
			// we will want to revist this - but since some things allow for searching
			// regardless of perms (like category id)...
			validator &&
				validator(rule[item], {
					spec1: attribute.spec1,
					spec2: attribute.spec2,
				});
		} catch (ex) {
			errors[item] = createValidationObject();
		}
	};

	// must have an attribute name to do anything
	if (!hasElementValue2)
		return {
			elementValue2: createValidationObject(),
		};

	// should not have values
	if (MetadataNoValueTypes.includes(rule.elementSubType1 as any)) {
		if (hasElementValue1) errors.elementValue1 = createValidationObject();
		if (hasElementValue3) errors.elementValue3 = createValidationObject();
	}
	// should have value(s)
	else {
		// must have two values
		if (rule.elementSubType1 === MetadataType.MATCH_ANY_RANGE) {
			!hasElementValue1
				? (errors.elementValue1 = createValidationObject())
				: validate('elementValue1');

			const compareStartValue = stringIsDate(rule.elementValue1)
				? new Date(rule.elementValue1).getTime()
				: Number(rule.elementValue1);
			const compareEndValue = stringIsDate(rule.elementValue3)
				? new Date(rule.elementValue3).getTime()
				: Number(rule.elementValue3);

			!hasElementValue3 || compareStartValue >= compareEndValue
				? (errors.elementValue3 = createValidationObject())
				: validate('elementValue3');
		}
		// must have a single value
		else if (!hasElementValue1) errors.elementValue1 = createValidationObject();
		// validate it
		else validate('elementValue1');
	}

	return Object.keys(errors).length ? errors : false;
};

export default validateMetadataRule;
